import { getRequest }          from '@helpers/javascript/javascript'
import { parameterizeFilter }  from '@helpers/javascript/javascript'
import { pathname }            from '@helpers/javascript/javascript'
import { serializeFilterParams } from '@helpers/javascript/javascript'
import { PATHS }               from '@helpers/javascript/config/CONSTANTS'
import { addNoticeAction }     from '@redux/notices/actions'
import { isOfflineWorkorder }  from '@helpers/javascript/workorder/helper'
import { advancedApplied, compactAdvanced, ADVANCED_PARAM_KEY } from '@helpers/javascript/advancedSearch'

// Dispatch
const fetchWorkordersBegin = () => ({
  type: WORKORDER_FETCH.BEGIN
})

const fetchWorkordersSuccess = workorders => ({
    type: WORKORDER_FETCH.SUCCESS,
    payload: { ...workorders }
})

const fetchWorkordersFailure = () => ({
  type: WORKORDER_FETCH.FAILURE
})

// Build the index URL: once the advanced search is applied it takes over and is
// serialized (via the shared serializeFilterParams) into workorders_filter_form[...]
// params; otherwise the quick filters. The `applied` sentinel makes the backend take
// the advanced branch even with no filter value, so a "clean" advanced search shows
// all interventions (matching the footer count) rather than the quick date view.
// Pass format: 'json' for the fetch URL and nothing for the browser URL. The two must
// not be the same string: `/mobile/interventions` serves the HTML shell first in its
// respond_to and sends no Vary: Accept, so the document cached under that URL can be
// handed back to a fetch asking for JSON. The extension pins the format to the path.
export const workordersIndexUrl = (filters, advancedFilters, format = null) => {
  const path = PATHS.workorders.index
  if (!advancedApplied(advancedFilters)) {
    const params = parameterizeFilter(filters)
    // The browser URL leaves today out; the fetch never does, so a refetch nobody
    // asked for cannot come back with a day other than the one the bar shows.
    if (format && filters.status === 'date' && !filters.search) params.date = filters.date

    return pathname({ path, format, params })
  }

  const query = serializeFilterParams({
    [ADVANCED_PARAM_KEY]: { ...compactAdvanced(advancedFilters), applied: 1 },
    page: filters.page
  })
  return `${format ? `${path}.${format}` : path}?${query}`
}

export const fetchWorkordersAction = filters => (dispatch, getState) => {
  const advanced = getState().advancedFilters
  history.replaceState(history.state, document.title, workordersIndexUrl(filters, advanced))
  dispatch(fetchWorkordersBegin())

  getRequest(workordersIndexUrl(filters, advanced, 'json'))
  .then (data => {
    // parseResponse turns a 200 that isn't JSON (the HTML shell, when it reaches the
    // fetch despite the `.json` above) into `{}`. Dispatching that as a success wipes
    // absences/total_pages, so treat a payload without workorders as a failure.
    if (!data?.workorders) return dispatch(fetchWorkordersFailure())

    document.body.scrollTo({ top: 0, behavior: 'smooth' })
    dispatch(fetchWorkordersSuccess(data))
  })
  // Without this catch a rejected request (e.g. a backend timeout, where
  // parseResponse throws "Load failed") leaves `loading` stuck on true and the
  // list spins forever. Reset the loading state and tell the user to retry.
  .catch(error => {
    dispatch(fetchWorkordersFailure())
    if (error?.message?.includes('Load failed')) {
      dispatch(addNoticeAction({
        message: I18n.t('mobile.list.workorders.load_failed'),
        type:    'error'
      }))
    }
  })
}

// Reducer response
export const WORKORDER_FETCH = {
  BEGIN:   'FETCH_WORKORDERS_BEGIN',
  SUCCESS: 'FETCH_WORKORDERS_SUCCESS',
  FAILURE: 'FETCH_WORKORDERS_FAILURE',

  clearResponse:   ({ state }) => ({...state, items: [] }),
  beginResponse:   ({ state }) => ({...state, loading: true }),
  failureResponse: ({ state }) => ({...state, loading: false, loadingNext: false }),

  successResponse: ({
    state,
    workorders,
    stored_workorders,
    absences,
    recap,
    total_entries,
    total_pages
  }) => {
    const isUnsynced = wo => wo.error || wo.syncing || !wo.synced || isOfflineWorkorder(wo)

    const mergeWorkorders = (locals, remotes) => {
      if(!remotes) return locals

      const merged = remotes.map(remote => {
        const local = locals.find(wo => wo.id == remote.id)
        if(!local)         return remote
        // An unsynced local holds changes the server doesn't have — keep it, never
        // clobber on a routine fetch (overwrite only on an explicit download-from-server).
        if(isUnsynced(local)) return local
        return remote
      })

      const keptLocals = locals.filter(local =>
        isUnsynced(local) && !remotes.some(remote => remote.id == local.id)
      )

      return [...merged, ...keptLocals]
    }

    return {
      ...state,
      total_entries,
      total_pages,
      loading:           false,
      loadingNext:       false,
      items:             mergeWorkorders(state.items, workorders),
      stored_workorders:  mergeWorkorders(state.stored_workorders, stored_workorders),
      absences:          absences || [],
      recap:             recap
 }
  }
}
